import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api-client';
import { useAuthContext } from '@/hooks/useAuthContext';

export interface PartnerOverviewData {
  partnerId: string;
  referralCode: string;
  referralUrl: string;
  tier: 'referral' | 'growth';
  tierDisplayName: string;
  commissionRatePercent: number;
  availableBalanceMinor: number;
  pendingClearanceMinor: number;
  lifetimeEarningsMinor: number;
  minimumPayoutMinor: number;
  totalClicks: number;
  totalLeads: number;
  activeSubscriptions: number;
  conversionRatePercent: number;
  payoutMethod: {
    type: 'bkash' | 'nagad' | 'bank';
    accountDetails: string;
  } | null;
  recentTransactions: Array<{
    id: string;
    date: string;
    description: string;
    amountMinor: number;
    type: 'credit' | 'debit';
  }>;
  recentLeads: Array<{
    id: string;
    customerName: string;
    plan: string;
    attributedDate: string;
    status: 'active' | 'trial' | 'cancelled';
  }>;
}

export function usePartnerOverview() {
  const { userId, orgId } = useAuthContext();
  const [data, setData] = useState<PartnerOverviewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultReferralCode = (orgId ? orgId.slice(0, 8) : (userId ? userId.slice(0, 8) : 'PARTNER')).toUpperCase();
  const defaultReferralUrl = `https://conversora.io/r/${defaultReferralCode.toLowerCase()}`;

  const fetchOverview = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await api.get<{
        success: boolean;
        data: PartnerOverviewData;
      }>('/api/v1/partner/overview');

      if (res?.success && res.data) {
        setData(res.data);
      } else {
        // Fallback default clean zero-state (never fake money)
        setData({
          partnerId: orgId || userId || '',
          referralCode: defaultReferralCode,
          referralUrl: defaultReferralUrl,
          tier: 'referral',
          tierDisplayName: 'Referral Partner',
          commissionRatePercent: 20,
          availableBalanceMinor: 0,
          pendingClearanceMinor: 0,
          lifetimeEarningsMinor: 0,
          minimumPayoutMinor: 100000,
          totalClicks: 0,
          totalLeads: 0,
          activeSubscriptions: 0,
          conversionRatePercent: 0,
          payoutMethod: null,
          recentTransactions: [],
          recentLeads: [],
        });
      }
    } catch {
      // In unauthenticated or offline preview state, show real 0 balance
      setData({
        partnerId: orgId || userId || '',
        referralCode: defaultReferralCode,
        referralUrl: defaultReferralUrl,
        tier: 'referral',
        tierDisplayName: 'Referral Partner',
        commissionRatePercent: 20,
        availableBalanceMinor: 0,
        pendingClearanceMinor: 0,
        lifetimeEarningsMinor: 0,
        minimumPayoutMinor: 100000,
        totalClicks: 0,
        totalLeads: 0,
        activeSubscriptions: 0,
        conversionRatePercent: 0,
        payoutMethod: null,
        recentTransactions: [],
        recentLeads: [],
      });
    } finally {
      setIsLoading(false);
    }
  }, [orgId, userId, defaultReferralCode, defaultReferralUrl]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return {
    data: data || {
      partnerId: orgId || userId || '',
      referralCode: defaultReferralCode,
      referralUrl: defaultReferralUrl,
      tier: 'referral',
      tierDisplayName: 'Referral Partner',
      commissionRatePercent: 20,
      availableBalanceMinor: 0,
      pendingClearanceMinor: 0,
      lifetimeEarningsMinor: 0,
      minimumPayoutMinor: 100000,
      totalClicks: 0,
      totalLeads: 0,
      activeSubscriptions: 0,
      conversionRatePercent: 0,
      payoutMethod: null,
      recentTransactions: [],
      recentLeads: [],
    },
    isLoading,
    error,
    refetch: fetchOverview,
  };
}
